import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AtlasCheckoutRequest, CheckoutPlan, loadOnboardingEmail, RegistrationService, saveOnboardingEmail, TenantInfo } from "../registration.service";

interface Status {
  loading: boolean;
}

@Component({
  selector: "app-atlas-subscribe",
  templateUrl: "./atlas-subscribe.component.html",
  styleUrls: ["./atlas-subscribe.component.css"],
  standalone: false,
})
export class AtlasSubscribeComponent implements OnInit {
  public status: Status = { loading: false };
  public selectedPlan: CheckoutPlan | null = null;
  public tenantId: string | null = null;
  public returnUrl: string | null = null;
  public plans: CheckoutPlan[] = [];
  public plansLoading = false;
  public plansError: string | null = null;
  public subscriptionBlocked = false;
  public subscriptionBlockMessage: string | null = null;
  public tenantInfo?: TenantInfo;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly snackbar: MatSnackBar,
    private readonly registrationService: RegistrationService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.tenantInfo = this.navigationTenantInfo();
    this.tenantId = this.route.snapshot.queryParamMap.get("tenantId");
    saveOnboardingEmail(this.tenantId || this.tenantInfo?.tenantName, this.tenantInfo?.emailAddress);
    if (!this.tenantId) {
      this.snackbar.open("Tenant id is missing. Return to the portal and try again.", "OK");
      this.returnUrl = document.referrer || null;
    }
    this.loadPlans();
  }

  loadPlans(): void {
    this.plansLoading = true;
    this.plansError = null;
    this.registrationService.getCheckoutPlans().subscribe({
      next: (response) => {
        this.plans = response?.plans || [];
        this.plansLoading = false;
      },
      error: () => {
        this.plansLoading = false;
        this.plansError = "Unable to load plans right now.";
      },
    });
  }

  selectPlan(plan: CheckoutPlan): void {
    this.selectedPlan = plan;
  }

  canContinueToCheckout(): boolean {
    return Boolean(
      this.selectedPlan?.subscriptionLevel &&
      this.tenantId &&
      !this.status.loading &&
      !this.subscriptionBlocked
    );
  }

  continueToCheckout(): void {
    if (!this.selectedPlan?.subscriptionLevel || !this.tenantId) {
      this.snackbar.open("Select a subscription level to continue.", "OK");
      return;
    }

    const payload: AtlasCheckoutRequest = {
      subscriptionLevel: this.selectedPlan.subscriptionLevel,
      priceId: this.selectedPlan.priceId || undefined,
      tenantName: this.tenantId,
      tenantInfo: this.tenantInfo,
    };

    this.status.loading = true;
    this.registrationService.createCheckoutSession(this.tenantId, payload).subscribe({
      next: (response) => {
        const checkoutUrl = response?.checkout_url;
        if (!checkoutUrl) {
          this.status.loading = false;
          this.snackbar.open("Checkout session was created without a redirect URL.", "OK");
          return;
        }

        window.location.assign(checkoutUrl);
      },
      error: (err) => {
        this.status.loading = false;
        const reason = err?.error?.reason || "Unable to start checkout right now.";
        if (err?.status === 409) {
          this.subscriptionBlocked = true;
          this.subscriptionBlockMessage = reason;
        }
        this.snackbar.open(`Checkout failed: ${reason}`, "OK");
      },
    });
  }

  formatPlanPrice(plan: CheckoutPlan): string {
    if (plan.unitAmount === null || plan.unitAmount === undefined) {
      return "Custom";
    }

    const currency = (plan.currency || "USD").toUpperCase();
    const amount = plan.unitAmount / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  }

  planInterval(plan: CheckoutPlan): string {
    return plan.interval || "month";
  }

  private navigationTenantInfo(): TenantInfo | undefined {
    const state = this.router.getCurrentNavigation()?.extras.state ?? history.state;
    const tenantInfo = state?.["tenantInfo"];
    if (tenantInfo && typeof tenantInfo === "object") {
      return tenantInfo as TenantInfo;
    }

    const emailAddress = this.route.snapshot.queryParamMap.get("email")?.trim();
    const tenantName = this.route.snapshot.queryParamMap.get("tenantId") ?? undefined;
    const resolvedEmail = loadOnboardingEmail(tenantName) ?? emailAddress;
    if (!resolvedEmail) {
      return undefined;
    }
    return {
      ...(tenantName ? { tenantName } : {}),
      emailAddress: resolvedEmail,
    };
  }
}
